import React, { useState } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  message,
  Row,
  Col,
  Statistic,
  Collapse,
} from 'antd';
import {
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CrownOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const STATUS_MAP = {
  pending: { color: 'orange', label: 'Pending' },
  provider_assigned: { color: 'purple', label: 'Provider Assigned' },
  provider_submitted: { color: 'blue', label: 'Provider Submitted' },
  approved: { color: 'green', label: 'Approved' },
  rejected: { color: 'red', label: 'Rejected' },
};

const formatNPR = (amount) => {
  if (!amount || amount <= 0) return '-';
  return 'Rs. ' + Number(amount).toLocaleString('en-IN');
};

const Membership = () => {
  const [statusFilter, setStatusFilter] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [assignModal, setAssignModal] = useState(false);
  const [approveModal, setApproveModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [assignForm] = Form.useForm();
  const [approveForm] = Form.useForm();
  const [rejectForm] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: assessments = [], isLoading } = useQuery(
    ['membership-requests', statusFilter],
    async () => {
      const params = statusFilter ? { status: statusFilter } : {};
      const response = await api.get('/admin/membership/requests', { params });
      return response.data?.data || [];
    },
  );

  const { data: stats } = useQuery('membership-stats', async () => {
    const response = await api.get('/admin/membership/statistics');
    return response.data?.data || {};
  });

  const { data: providers = [] } = useQuery('providers-list', async () => {
    const response = await api.get('/admin/providers');
    return response.data?.data || [];
  });

  const { data: tiers = [] } = useQuery('pricing-tiers', async () => {
    const response = await api.get('/membership/tiers');
    return response.data?.data || [];
  });

  const assignMutation = useMutation(
    async ({ id, ...values }) => {
      const response = await api.post(`/admin/membership/requests/${id}/assign-provider`, values);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Provider assigned successfully');
        setAssignModal(false);
        assignForm.resetFields();
        queryClient.invalidateQueries(['membership-requests']);
        queryClient.invalidateQueries('membership-stats');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to assign provider');
      },
    },
  );

  const approveMutation = useMutation(
    async ({ id, ...values }) => {
      const response = await api.post(`/admin/membership/requests/${id}/approve`, values);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Membership approved successfully');
        setApproveModal(false);
        queryClient.invalidateQueries(['membership-requests']);
        queryClient.invalidateQueries('membership-stats');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to approve');
      },
    },
  );

  const rejectMutation = useMutation(
    async ({ id, ...values }) => {
      const response = await api.post(`/admin/membership/requests/${id}/reject`, values);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Request rejected');
        setRejectModal(false);
        rejectForm.resetFields();
        queryClient.invalidateQueries(['membership-requests']);
        queryClient.invalidateQueries('membership-stats');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to reject');
      },
    },
  );

  const statusTabs = [
    { key: null, label: 'All', icon: <FileTextOutlined /> },
    { key: 'pending', label: 'Pending', icon: <ClockCircleOutlined /> },
    { key: 'provider_assigned', label: 'Assigned', icon: <TeamOutlined /> },
    { key: 'provider_submitted', label: 'Submitted', icon: <FileTextOutlined /> },
    { key: 'approved', label: 'Approved', icon: <CrownOutlined /> },
    { key: 'rejected', label: 'Rejected', icon: <CloseCircleOutlined /> },
  ];

  const columns = [
    {
      title: 'Customer',
      key: 'customer',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <span>{r.customer?.name || r.phone || 'N/A'}</span>
          <span style={{ fontSize: 12, color: '#888' }}>{r.customer?.phone || r.phone}</span>
        </Space>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontSize: 12 }}>{r.phone}</span>
          <span style={{ fontSize: 12, color: '#888' }}>{r.address}</span>
        </Space>
      ),
    },
    {
      title: 'Points',
      dataIndex: 'assessment_points',
      key: 'points',
      render: (p) => (p > 0 ? <Tag color="blue">{p} pts</Tag> : <Tag>Pending</Tag>),
    },
    {
      title: 'Yearly Price',
      key: 'price',
      render: (_, r) => (r.yearly_price > 0 ? formatNPR(r.yearly_price) + '/yr' : '-'),
    },
    {
      title: 'Provider',
      key: 'provider',
      render: (_, r) => r.assigned_provider?.name || <Tag>Unassigned</Tag>,
    },
    {
      title: 'Referral',
      dataIndex: 'referral_code',
      key: 'referral',
      render: (code) => (code ? <Tag color="green">{code}</Tag> : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s) => {
        const meta = STATUS_MAP[s] || { color: 'default', label: s };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'date',
      render: (d) => dayjs(d).format('MMM D, YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 300,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            onClick={() => {
              setSelectedAssessment(record);
              setDetailModal(true);
            }}
          >
            View
          </Button>
          {record.status === 'pending' && (
            <Button
              type="primary"
              size="small"
              icon={<TeamOutlined />}
              onClick={() => {
                setSelectedAssessment(record);
                assignForm.setFieldsValue({ assigned_provider_id: undefined });
                setAssignModal(true);
              }}
            >
              Assign
            </Button>
          )}
          {record.status === 'provider_submitted' && (
            <>
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => {
                  setSelectedAssessment(record);
                  approveForm.resetFields();
                  setApproveModal(true);
                }}
              >
                Approve
              </Button>
              <Button
                danger
                size="small"
                icon={<CloseCircleOutlined />}
                onClick={() => {
                  setSelectedAssessment(record);
                  rejectForm.resetFields();
                  setRejectModal(true);
                }}
              >
                Reject
              </Button>
            </>
          )}
          {record.status === 'approved' && <Tag color="green">Active</Tag>}
          {record.status === 'rejected' && <Tag color="red">Rejected</Tag>}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Membership Management</Title>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={4}>
          <Card>
            <Statistic title="Total" value={stats?.total || 0} prefix={<FileTextOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={4}>
          <Card>
            <Statistic
              title="Pending"
              value={stats?.pending || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={4}>
          <Card>
            <Statistic
              title="Assigned"
              value={stats?.provider_assigned || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={4}>
          <Card>
            <Statistic
              title="Submitted"
              value={stats?.provider_submitted || 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={4}>
          <Card>
            <Statistic
              title="Approved"
              value={stats?.approved || 0}
              prefix={<CrownOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={4}>
          <Card>
            <Statistic
              title="Rejected"
              value={stats?.rejected || 0}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        tabList={statusTabs}
        activeTabKey={statusFilter}
        onTabChange={(key) => setStatusFilter(key)}
        tabBarExtraContent={
          <Collapse
            ghost
            size="small"
            items={[
              {
                key: 'pricing',
                label: (
                  <span>
                    <DollarOutlined /> Pricing Tiers
                  </span>
                ),
                children: (
                  <Table
                    dataSource={tiers}
                    columns={[
                      {
                        title: 'Points Range',
                        key: 'range',
                        render: (_, r) =>
                          r.max_points ? `${r.min_points} - ${r.max_points}` : `${r.min_points}+`,
                      },
                      {
                        title: 'Monthly',
                        key: 'monthly',
                        render: (_, r) => `Rs. ${r.monthly_price}`,
                      },
                      {
                        title: 'Yearly',
                        key: 'yearly',
                        render: (_, r) => (
                          <Text strong>Rs. {Number(r.yearly_price).toLocaleString('en-IN')}</Text>
                        ),
                      },
                    ]}
                    rowKey="min_points"
                    pagination={false}
                    size="small"
                  />
                ),
              },
            ]}
          />
        }
      >
        <Table
          columns={columns}
          dataSource={assessments}
          loading={isLoading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t) => `${t} requests`,
          }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title="Request Details"
        open={detailModal}
        onCancel={() => setDetailModal(false)}
        footer={null}
        width={640}
      >
        {selectedAssessment && (
          <div>
            <Row gutter={16}>
              <Col span={12}>
                <p>
                  <strong>Customer:</strong> {selectedAssessment.customer?.name || 'N/A'}
                </p>
                <p>
                  <strong>Email:</strong> {selectedAssessment.customer?.email || 'N/A'}
                </p>
                <p>
                  <strong>Phone:</strong> {selectedAssessment.phone}
                </p>
                <p>
                  <strong>Address:</strong> {selectedAssessment.address}
                </p>
              </Col>
              <Col span={12}>
                <p>
                  <strong>Referral Code:</strong> {selectedAssessment.referral_code || 'None'}
                </p>
                <p>
                  <strong>Points:</strong> {selectedAssessment.assessment_points || 'Pending'}
                </p>
                <p>
                  <strong>Yearly Price:</strong>{' '}
                  {selectedAssessment.yearly_price
                    ? `Rs. ${Number(selectedAssessment.yearly_price).toLocaleString('en-IN')}`
                    : 'Pending'}
                </p>
                <p>
                  <strong>Duration:</strong>{' '}
                  {selectedAssessment.duration_years
                    ? `${selectedAssessment.duration_years} year(s) (${selectedAssessment.duration_days} days)`
                    : '1 year (365 days)'}
                </p>
              </Col>
            </Row>
            {selectedAssessment.referral_bonus_days > 0 && (
              <p>
                <strong>Referral Bonus:</strong> +{selectedAssessment.referral_bonus_days} days for
                referrer
              </p>
            )}
            <p>
              <strong>Provider:</strong>{' '}
              {selectedAssessment.assigned_provider?.name || 'Not assigned'}
            </p>
            {selectedAssessment.provider_notes && (
              <p>
                <strong>Provider Notes:</strong> {selectedAssessment.provider_notes}
              </p>
            )}
            {selectedAssessment.admin_notes && (
              <p>
                <strong>Admin Notes:</strong> {selectedAssessment.admin_notes}
              </p>
            )}
            {selectedAssessment.rejection_reason && (
              <p>
                <strong>Rejection Reason:</strong> {selectedAssessment.rejection_reason}
              </p>
            )}
          </div>
        )}
      </Modal>

      {/* Assign Provider Modal */}
      <Modal
        title="Assign Provider"
        open={assignModal}
        onCancel={() => setAssignModal(false)}
        footer={null}
      >
        <Form
          form={assignForm}
          layout="vertical"
          onFinish={(values) => {
            assignMutation.mutate({ id: selectedAssessment.id, ...values });
          }}
        >
          <Form.Item
            name="assigned_provider_id"
            label="Provider"
            rules={[{ required: true, message: 'Select a provider' }]}
          >
            <Select showSearch placeholder="Select provider..." optionFilterProp="children">
              {providers
                .filter((p) => p.is_active !== false)
                .map((p) => (
                  <Option key={p.id} value={p.id}>
                    {p.name || p.full_name} - {p.phone_number || p.phone || p.email}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={assignMutation.isLoading}>
                Assign
              </Button>
              <Button onClick={() => setAssignModal(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Approve Modal */}
      <Modal
        title="Approve Membership"
        open={approveModal}
        onCancel={() => setApproveModal(false)}
        footer={null}
      >
        <Form
          form={approveForm}
          layout="vertical"
          onFinish={(values) => {
            approveMutation.mutate({ id: selectedAssessment.id, ...values });
          }}
        >
          <p>
            Points: <strong>{selectedAssessment?.assessment_points || 0}</strong>
          </p>
          <p>
            Yearly Price:{' '}
            <strong>
              {selectedAssessment?.yearly_price
                ? 'Rs. ' + Number(selectedAssessment.yearly_price).toLocaleString('en-IN')
                : 'N/A'}
            </strong>
          </p>
          <Form.Item
            name="duration_years"
            label="Duration (Years)"
            initialValue={selectedAssessment?.duration_years || 1}
            rules={[{ required: true, message: 'Please enter duration in years' }]}
          >
            <InputNumber min={1} max={10} style={{ width: '100%' }} />
          </Form.Item>
          {selectedAssessment?.referral_bonus_days > 0 && (
            <p>
              Referrer bonus: <strong>+{selectedAssessment.referral_bonus_days} days</strong>
            </p>
          )}
          <Form.Item name="admin_notes" label="Admin Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={approveMutation.isLoading}>
                Approve & Activate
              </Button>
              <Button onClick={() => setApproveModal(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Reject Modal */}
      <Modal
        title="Reject Request"
        open={rejectModal}
        onCancel={() => setRejectModal(false)}
        footer={null}
      >
        <Form
          form={rejectForm}
          layout="vertical"
          onFinish={(values) => {
            rejectMutation.mutate({ id: selectedAssessment.id, ...values });
          }}
        >
          <Form.Item
            name="rejection_reason"
            label="Rejection Reason"
            rules={[{ required: true, message: 'Please provide a reason' }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="admin_notes" label="Admin Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button danger type="primary" htmlType="submit" loading={rejectMutation.isLoading}>
                Reject
              </Button>
              <Button onClick={() => setRejectModal(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Membership;
